// Taker /check-in?t=<token> — the member check-in form (thread
// mct_42ee6fc4-c7e; SOP seeds/sops/sop-ee-member-checkins.md).
//
// A program member gets "A quick check-in on <program>" by email with a
// personal link. This page shows that check-in's questions and posts the
// answers back; the answer sets the member's health status for the TSB team.
// The token is the whole identity — no sign-in, no email field.
//
// Standard taker rules: no dark mode; useBrandGtm() opted in explicitly (the
// payload carries no GTM id, so the host fallback decides); mobile first —
// most of these get answered from a phone straight out of the inbox.
// recordLandingView() is deliberately NOT called here — it's keyed to an
// assessment slug, and this page has no assessment.

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BrandProvider } from './components/BrandContext';
import type { ResolvedBrand } from './types';
import { applyBranding } from './theme';
import { useBrandGtm } from './lib/useBrandGtm';

const API = import.meta.env.VITE_API_BASE_URL as string;

type QuestionType = 'radio' | 'text' | 'number' | 'number_pair';

interface CheckinOption { id: string; label: string; ee_key: null }

interface CheckinQuestion {
  id: string;
  prompt: string;
  sub_prompt: string | null;
  type: QuestionType;
  required: boolean;
  placeholder: string | null;
  goal_placeholder: string | null;
  verifies_milestone_id: string | null;
  options: CheckinOption[];
}

interface CheckinBrand {
  name: string | null;
  logoUrl: string | null;
  logoLinkUrl: string | null;
  palette: Record<string, unknown> | null;
  fonts: Record<string, unknown> | null;
  designTokens: Record<string, unknown> | null;
  googleFontsUrl: string | null;
}

interface CheckinPayload {
  state: 'open' | 'answered' | 'closed';
  kind: 'checkin' | 'outcome';
  first_name: string | null;
  program_slug: string;
  program_name: string;
  survey_name: string | null;
  questions: CheckinQuestion[];
  brand: CheckinBrand | null;
}

type PairValue = { now: number | null; goal: number | null };
type AnswerValue = string | number | PairValue;

function toResolvedBrand(b: CheckinBrand): ResolvedBrand {
  return {
    name: b.name,
    logoUrl: b.logoUrl,
    logoWhiteUrl: null,
    logoLinkUrl: b.logoLinkUrl,
    palette: b.palette as ResolvedBrand['palette'],
    fonts: b.fonts as ResolvedBrand['fonts'],
    designTokens: b.designTokens as ResolvedBrand['designTokens'],
    googleFontsUrl: b.googleFontsUrl,
    gtmContainerId: null,
  };
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '12px 13px', border: '1px solid var(--color-border, #ddd)',
  borderRadius: 8, fontSize: 16, boxSizing: 'border-box', fontFamily: 'var(--font-body)',
  background: '#fff',
};

const centredWrap: React.CSSProperties = {
  maxWidth: 480, margin: '80px auto', padding: '0 20px', textAlign: 'center',
};

function Logo({ brand }: { brand: CheckinBrand | null }) {
  if (!brand?.logoUrl) return null;
  const img = <img src={brand.logoUrl} alt={brand.name ?? ''} style={{ height: 32, marginBottom: 28 }} />;
  return brand.logoLinkUrl ? <a href={brand.logoLinkUrl}>{img}</a> : img;
}

/** One centred message screen — invalid / answered / closed / done. */
function Message({ brand, heading, body }: { brand: CheckinBrand | null; heading: string; body: string }) {
  return (
    <div style={centredWrap}>
      <Logo brand={brand} />
      <h1 style={{ fontSize: 22, fontFamily: 'var(--font-heading)', margin: '0 0 10px' }}>{heading}</h1>
      <p style={{ color: '#666', lineHeight: 1.6, margin: 0 }}>{body}</p>
    </div>
  );
}

export default function CheckinPage() {
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';
  const [payload, setPayload] = useState<CheckinPayload | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [loadErr, setLoadErr] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setInvalid(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadErr('');
    setInvalid(false);
    fetch(`${API}/api/check-in?t=${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (r.status === 404) {
          setInvalid(true);
          return;
        }
        if (!r.ok) throw new Error('We couldn\'t load your check-in just now. Please try again in a moment.');
        setPayload((await r.json()) as CheckinPayload);
      })
      .catch((e: Error) => setLoadErr(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (payload?.brand) applyBranding(undefined, { brand: toResolvedBrand(payload.brand) as never });
  }, [payload?.brand]);

  useBrandGtm(null, !loading);

  if (loading) {
    return <div style={{ padding: 60, textAlign: 'center', color: '#888' }}>Loading…</div>;
  }
  if (invalid) {
    return (
      <Message
        brand={null}
        heading="This check-in link isn't working"
        body="It may have expired. If you'd like to tell us how things are going, just reply to the email it came in."
      />
    );
  }
  if (loadErr || !payload) {
    return <Message brand={null} heading="Something went wrong" body={loadErr || 'Please try the link again in a moment.'} />;
  }

  const content = (() => {
    if (payload.state === 'answered') {
      return (
        <Message
          brand={payload.brand}
          heading="Thanks — we already have your answer"
          body={`Your check-in for ${payload.program_name} is in. If anything has changed, reply to the email and the team will see it.`}
        />
      );
    }
    if (payload.state === 'closed') {
      return (
        <Message
          brand={payload.brand}
          heading="This check-in has closed"
          body="If you'd still like to tell us how things are going, just reply to the email it came in — the team will see it."
        />
      );
    }
    return <CheckinForm payload={payload} token={token} />;
  })();

  return payload.brand
    ? <BrandProvider brand={toResolvedBrand(payload.brand)}>{content}</BrandProvider>
    : content;
}

function isAnswered(q: CheckinQuestion, v: AnswerValue | undefined): boolean {
  if (v === undefined) return false;
  if (q.type === 'number_pair') {
    const p = v as PairValue;
    return p.now !== null || p.goal !== null;
  }
  if (typeof v === 'string') return v.trim().length > 0;
  return true;
}

function parseNum(raw: string): number | null {
  const s = raw.trim().replace(/,/g, '');
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function CheckinForm({ payload, token }: { payload: CheckinPayload; token: string }) {
  const { brand, questions, first_name, program_name } = payload;
  // Raw text for number inputs, so a half-typed "1." isn't eaten by parsing.
  const [raw, setRaw] = useState<Record<string, string>>({});
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [missing, setMissing] = useState<string[]>([]);
  const [problems, setProblems] = useState<string[]>([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  function valueFor(q: CheckinQuestion): AnswerValue | undefined {
    switch (q.type) {
      case 'radio':
        return choice[q.id];
      case 'text': {
        const t = raw[q.id];
        return t === undefined ? undefined : t;
      }
      case 'number': {
        const n = parseNum(raw[q.id] ?? '');
        return n === null ? undefined : n;
      }
      case 'number_pair':
        return { now: parseNum(raw[`${q.id}:now`] ?? ''), goal: parseNum(raw[`${q.id}:goal`] ?? '') };
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr('');
    setProblems([]);
    const answers: Record<string, AnswerValue> = {};
    const gaps: string[] = [];
    for (const q of questions) {
      const v = valueFor(q);
      if (isAnswered(q, v)) answers[q.id] = typeof v === 'string' ? v.trim() : (v as AnswerValue);
      else if (q.required) gaps.push(q.prompt);
    }
    setMissing(gaps);
    if (gaps.length) return;

    setBusy(true);
    try {
      const res = await fetch(`${API}/api/check-in/submit?t=${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers }),
      });
      const data = await res.json().catch(() => null) as
        | { ok: boolean; reason?: string; problems?: string[] }
        | null;
      if (res.ok && data?.ok) {
        setDone(true);
        return;
      }
      if (res.status === 404) {
        setErr("This check-in link isn't working any more. If you'd like to tell us how things are going, just reply to the email it came in.");
      } else if (data?.reason === 'closed') {
        setErr('This check-in has closed. If you would still like to tell us how things are going, just reply to the email it came in.');
      } else if (data?.reason === 'invalid-answers' && data.problems?.length) {
        setProblems(data.problems);
      } else {
        setErr('Something went wrong sending that. Please try again in a moment.');
      }
    } catch {
      setErr('Something went wrong sending that. Please check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <Message
        brand={brand}
        heading={`Thank you${first_name ? `, ${first_name}` : ''}.`}
        body="Your answer is with the team — thank you for taking a minute to tell us how it's going."
      />
    );
  }

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', padding: '36px 18px 80px', fontFamily: 'var(--font-body)' }}>
      <Logo brand={brand} />
      <h1 style={{ fontSize: 24, fontFamily: 'var(--font-heading)', margin: '0 0 8px', lineHeight: 1.25 }}>
        Hi {first_name || 'there'}, how's {program_name} going?
      </h1>
      <p style={{ fontSize: 15, color: '#555', margin: '0 0 28px', lineHeight: 1.5 }}>
        A short check-in — it takes about a minute, and the team reads every answer.
      </p>

      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 28 }} noValidate>
        {questions.map((q) => (
          <fieldset key={q.id} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
            <legend style={{ fontSize: 16, fontWeight: 700, padding: 0, marginBottom: q.sub_prompt ? 4 : 10, lineHeight: 1.4 }}>
              {q.prompt}
              {!q.required && (
                <span style={{ fontWeight: 400, fontSize: 13, color: '#999', marginLeft: 8 }}>optional</span>
              )}
            </legend>
            {q.sub_prompt && (
              <p style={{ fontSize: 13.5, color: '#777', margin: '0 0 10px', lineHeight: 1.5 }}>{q.sub_prompt}</p>
            )}

            {q.type === 'radio' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} role="radiogroup">
                {q.options.map((o) => {
                  const selected = choice[q.id] === o.id;
                  return (
                    <label
                      key={o.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, minHeight: 48,
                        padding: '12px 14px', borderRadius: 10, cursor: 'pointer', boxSizing: 'border-box',
                        border: selected ? '2px solid var(--color-primary)' : '1px solid var(--color-border, #ddd)',
                        background: selected ? 'var(--color-tint, #f5f5f7)' : '#fff',
                        // Keep the text from shifting when the border thickens.
                        margin: selected ? 0 : 1,
                        fontSize: 15, lineHeight: 1.4,
                      }}
                    >
                      <input
                        type="radio"
                        name={q.id}
                        value={o.id}
                        checked={selected}
                        onChange={() => setChoice((c) => ({ ...c, [q.id]: o.id }))}
                        style={{ accentColor: 'var(--color-primary)', width: 18, height: 18, margin: 0, flexShrink: 0 }}
                      />
                      <span>{o.label}</span>
                    </label>
                  );
                })}
              </div>
            )}

            {q.type === 'text' && (
              <textarea
                rows={4}
                style={{ ...inputStyle, resize: 'vertical' }}
                placeholder={q.placeholder ?? undefined}
                value={raw[q.id] ?? ''}
                onChange={(e) => setRaw((r) => ({ ...r, [q.id]: e.target.value }))}
              />
            )}

            {q.type === 'number' && (
              <input
                type="text"
                inputMode="decimal"
                style={{ ...inputStyle, maxWidth: 220 }}
                placeholder={q.placeholder ?? undefined}
                value={raw[q.id] ?? ''}
                onChange={(e) => setRaw((r) => ({ ...r, [q.id]: e.target.value }))}
              />
            )}

            {q.type === 'number_pair' && (
              <div style={{ display: 'flex', gap: 12 }}>
                {([
                  ['now', q.placeholder || 'Now'],
                  ['goal', q.goal_placeholder || 'Goal'],
                ] as const).map(([part, label]) => (
                  <label key={part} style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#555' }}>{label}</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      style={inputStyle}
                      value={raw[`${q.id}:${part}`] ?? ''}
                      onChange={(e) => setRaw((r) => ({ ...r, [`${q.id}:${part}`]: e.target.value }))}
                    />
                  </label>
                ))}
              </div>
            )}
          </fieldset>
        ))}

        {missing.length > 0 && (
          <div style={{ fontSize: 14, color: '#8a5a00', background: '#fff8e6', borderRadius: 8, padding: '12px 14px', lineHeight: 1.5 }}>
            Just {missing.length === 1 ? 'one more' : 'a few more'} to go — please answer:
            <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
              {missing.map((m) => <li key={m}>{m}</li>)}
            </ul>
          </div>
        )}

        {problems.length > 0 && (
          <div style={{ fontSize: 14, color: '#b00020', lineHeight: 1.5 }}>
            A couple of answers need another look:
            <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
              {problems.map((p) => <li key={p}>{p}</li>)}
            </ul>
          </div>
        )}

        {err && <div style={{ color: '#b00020', fontSize: 14, lineHeight: 1.5 }}>{err}</div>}

        <button
          type="submit"
          disabled={busy}
          style={{
            background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 8,
            padding: '14px 20px', minHeight: 48, fontSize: 16, fontWeight: 700,
            cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1, fontFamily: 'var(--font-body)',
          }}
        >
          {busy ? 'Sending…' : 'Send my check-in'}
        </button>
      </form>
    </div>
  );
}
